import type { BookingCostKind, PaidStatus } from '../types';
export type { BookingCostKind } from '../types';

/** Linked totals are edited in the shared expense editor, never on one side alone. */
export function assertLinkedCostUnchanged(expense: { amountBasis?: string } | null | undefined,
  before: object | null | undefined, after: object | null | undefined, partial = false): void {
  if (!expense || !before || !after) return;
  const previous = before as Record<string, unknown>, next = after as Record<string, unknown>;
  const keys = ['costAmount', 'costCurrency', 'costKind', 'paidStatus', 'depositAmount', 'depositCurrency'];
  const value = (item: Record<string, unknown>, key: string) => {
    if (key === 'costKind') return item.costKind || costKindForPaymentStatus(item.paidStatus as PaidStatus);
    if (key === 'depositCurrency' && item.paidStatus === 'Deposit paid') return item.depositCurrency || item.costCurrency;
    return item[key] === '' ? undefined : item[key];
  };
  if (keys.some(key => (!partial || (next[key] != null && next[key] !== '')) && value(previous, key) !== value(next, key))) {
    throw new Error('Edit cost and payment in the linked expense, then reopen this item.');
  }
}

/** The same money vocabulary in manual forms, import review and saved items. */
export const BOOKING_COST_LABELS: Record<BookingCostKind, { status: string; amount: string }> = {
  unclear: { status: 'Payment not confirmed', amount: 'Total cost' },
  paid: { status: 'Paid in full', amount: 'Total cost' },
  deposit: { status: 'Deposit paid', amount: 'Total cost' },
  due: { status: 'Not paid', amount: 'Total cost' },
  hold: { status: 'Refundable card hold — not a charge', amount: 'Card hold' },
  quote: { status: 'Quote or estimate — not a charge', amount: 'Estimated cost' },
};

export function costKindForPaymentStatus(status?: PaidStatus | null): BookingCostKind {
  if (status === 'Paid in Full') return 'paid';
  if (status === 'Deposit paid') return 'deposit';
  if (status === 'Not paid') return 'due';
  return 'unclear';
}

export function paymentStatusForCostKind(kind: BookingCostKind): PaidStatus | undefined {
  if (kind === 'paid') return 'Paid in Full';
  if (kind === 'deposit') return 'Deposit paid';
  if (kind === 'due') return 'Not paid';
  return undefined;
}

/** New expenses represent the agreed total; provider payments never reduce it. */
export function chargeToLog(
  kind: BookingCostKind, total: number, currency: string,
  deposit?: number, depositCurrency?: string,
): { amount: number; currency: string } | null {
  if (kind !== 'paid' && kind !== 'deposit' && kind !== 'due') return null;
  if (!Number.isFinite(total) || total <= 0) throw new Error('Enter a valid total amount.');
  const totalCurrency = currency.trim().toUpperCase();
  if (!/^[A-Z]{3,5}$/.test(totalCurrency)) throw new Error('Enter a valid cost currency.');
  if (kind === 'deposit') {
    if (deposit == null || !Number.isFinite(deposit) || deposit <= 0) throw new Error('Enter the deposit actually charged.');
    const paidCurrency = (depositCurrency || totalCurrency).trim().toUpperCase();
    if (!/^[A-Z]{3,5}$/.test(paidCurrency)) throw new Error('Enter a valid deposit currency.');
    if (paidCurrency === totalCurrency && deposit > total) throw new Error('Paid so far cannot exceed the total cost.');
  }
  return { amount: total, currency: totalCurrency };
}
